import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, TextField, Button, Typography, Alert,
  CircularProgress, Divider,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { apiClient, OtpChallenge } from '@services/api/client';
import { OtpCodeStep } from '@components/auth/OtpCodeStep';

export const LoginSelectorPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required');
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.adminLogin(email.trim(), password);
      if (res.otpRequired) setChallenge(res.challenge);
      else navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.error?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Background photo */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(/background.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Blue overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(15, 35, 80, 0.72)',
          backdropFilter: 'blur(1px)',
        }}
      />

      {/* Login card — sits above the overlay */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 420,
          mx: 2,
        }}
      >
        <Card
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: 'rgba(255,255,255,0.97)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
          }}
        >
          {/* Logo + title */}
          <Box sx={{ textAlign: 'center', mb: 3.5 }}>
            <img
              src="/logo.jpeg"
              alt="SDICS"
              style={{ width: 80, height: 80, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B3A6B', letterSpacing: 1 }}>
              SDICS
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
              Citizen Registration &amp; Monitoring System
            </Typography>
          </Box>

          {/* Admin login section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AdminPanelSettingsIcon sx={{ color: '#1B3A6B', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1B3A6B' }}>
              Administrator Login
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {challenge ? (
            <OtpCodeStep
              challenge={challenge}
              submitLabel="Verify and open dashboard"
              onVerify={async (code) => {
                await apiClient.verifyAdminOtp(challenge.otp_token, code);
                navigate('/', { replace: true });
              }}
              onBack={() => { setChallenge(null); setPassword(''); }}
            />
          ) : (
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              size="small"
              disabled={loading}
              autoComplete="email"
              autoFocus
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              size="small"
              disabled={loading}
              autoComplete="current-password"
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                mt: 2.5,
                py: 1.4,
                fontWeight: 700,
                fontSize: '0.95rem',
                backgroundColor: '#1B3A6B',
                '&:hover': { backgroundColor: '#0F2844' },
              }}
            >
              {loading
                ? <CircularProgress size={20} sx={{ color: 'white' }} />
                : 'Sign In to Dashboard'}
            </Button>
          </form>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              Field officers use the Android app distributed by the administrator via the dashboard.
            </Typography>
          </Box>
        </Card>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: 'rgba(255,255,255,0.5)', mt: 2 }}
        >
          © {new Date().getFullYear()} SDICS. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};
